import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import { IoClose, IoShareSocialOutline } from 'react-icons/io5';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { CgMenuGridR } from 'react-icons/cg';
import '../listing-gallery.css';

const ListingGallery = ({ images, title }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);

  const handleMobileScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const itemWidth = e.target.clientWidth;
    const newIndex = Math.round(scrollLeft / itemWidth);
    if (newIndex !== activeMobileIndex && newIndex < images.length) {
      setActiveMobileIndex(newIndex);
    }
  };

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'ArrowLeft') {
      handlePrev();
    } else if (e.key === 'Escape') {
      setLightboxOpen(false);
    }
  }, [lightboxOpen, currentIndex, images]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Handle body scroll locking when lightbox is open
  useEffect(() => {
    const preventDefault = (e) => {
      // Allow scrolling inside the thumbnails container
      if (e.target.closest('.lightbox-thumbnails')) {
        return;
      }
      e.preventDefault();
    };

    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      // Use non-passive event listener to allow preventDefault on touch/wheel
      window.addEventListener('touchmove', preventDefault, { passive: false });
      window.addEventListener('wheel', preventDefault, { passive: false });
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('touchmove', preventDefault);
      window.removeEventListener('wheel', preventDefault);
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('touchmove', preventDefault);
      window.removeEventListener('wheel', preventDefault);
    };
  }, [lightboxOpen]);

  if (!images || images.length === 0) {
    return null;
  }

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleNext = (e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = (e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'Listing',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Determine layout based on number of images
  const renderGrid = () => {
    const imgCount = images.length;

    if (imgCount === 1) {
      return (
        <div className="listing-gallery-grid layout-1">
          <div className="gallery-item" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt="Listing 1" />
          </div>
        </div>
      );
    }

    if (imgCount === 2) {
      return (
        <div className="listing-gallery-grid layout-2">
          <div className="gallery-item" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt="Listing 1" />
          </div>
          <div className="gallery-item" onClick={() => openLightbox(1)}>
            <img src={images[1]} alt="Listing 2" />
          </div>
        </div>
      );
    }

    if (imgCount === 3) {
      return (
        <div className="listing-gallery-grid layout-3">
          <div className="gallery-item main-img" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt="Listing 1" />
          </div>
          <div className="gallery-side">
            <div className="gallery-item" onClick={() => openLightbox(1)}>
              <img src={images[1]} alt="Listing 2" />
            </div>
            <div className="gallery-item" onClick={() => openLightbox(2)}>
              <img src={images[2]} alt="Listing 3" />
            </div>
          </div>
        </div>
      );
    }

    // 4 or more images
    return (
      <div className="listing-gallery-grid layout-4">
        <div className="gallery-item main-img" onClick={() => openLightbox(0)}>
          <img src={images[0]} alt="Listing 1" />
        </div>
        <div className="gallery-side-complex">
          <div className="gallery-item side-top" onClick={() => openLightbox(1)}>
            <img src={images[1]} alt="Listing 2" />
          </div>
          <div className="side-bottom">
            <div className="gallery-item" onClick={() => openLightbox(2)}>
              <img src={images[2]} alt="Listing 3" />
            </div>
            <div className="gallery-item position-relative" onClick={() => openLightbox(3)}>
              <img src={images[3]} alt="Listing 4" />
              {imgCount >= 4 && (
                <button className="show-all-btn" onClick={(e) => {
                  e.stopPropagation();
                  openLightbox(0);
                }}>
                  <CgMenuGridR className="me-2" size={20} />
                  Show all photos
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileCarousel = () => {
    return (
      <div className="mobile-carousel-wrapper">
        <div className="mobile-carousel" onScroll={handleMobileScroll}>
          {images.map((img, idx) => (
            <div key={idx} className="mobile-carousel-item" onClick={() => openLightbox(idx)}>
              <img src={img} alt={`Listing ${idx + 1}`} />
            </div>
          ))}
        </div>
        <div className="mobile-carousel-badge">
          {activeMobileIndex + 1} / {images.length}
        </div>
        <button className="mobile-show-all-btn" onClick={(e) => {
          e.stopPropagation();
          openLightbox(0);
        }}>
          <CgMenuGridR className="me-1" size={18} />
          Show all photos
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="my-4">
        <div className="d-none d-md-block listing-gallery-container">
          {renderGrid()}
        </div>
        <div className="d-block d-md-none">
          {renderMobileCarousel()}
        </div>
      </div>

      {lightboxOpen && (
        <div className="custom-lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-header" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-title-area">
              <h5 className="mb-0 text-white fw-bold">{title || 'Listing Images'}</h5>
              <span className="text-white-50">{currentIndex + 1} / {images.length}</span>
            </div>
            <div className="lightbox-actions">
              <button className="lightbox-icon-btn" onClick={handleShare}>
                <IoShareSocialOutline size={22} />
                <span className="ms-2">Share</span>
              </button>
              <button className="lightbox-icon-btn lightbox-close-btn" onClick={closeLightbox}>
                <IoClose size={26} />
              </button>
            </div>
          </div>

          <div className="lightbox-main-content">
            <button className="lightbox-nav-btn left" onClick={handlePrev}>
              <FaChevronLeft size={24} />
            </button>
            
            <div className="lightbox-image-container" onClick={(e) => e.stopPropagation()}>
              <img src={images[currentIndex]} alt={`Slide ${currentIndex + 1}`} className="lightbox-current-image" />
            </div>

            <button className="lightbox-nav-btn right" onClick={handleNext}>
              <FaChevronRight size={24} />
            </button>
          </div>

          <div className="lightbox-thumbnails-wrapper" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-thumbnails">
              {images.map((img, idx) => (
                <div 
                  key={idx} 
                  className={`thumbnail-item ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListingGallery;
